// Geocoding service with multiple providers
// Primary: OS Places API (Ordnance Survey) - Most accurate for UK
// Fallback: Nominatim (OpenStreetMap) - Free backup

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search'
const OS_PLACES_URL = 'https://api.os.uk/search/places/v1'

// Get OS Places API key from localStorage/settings
function getOSPlacesKey() {
  // Check if API key is stored in localStorage
  const settings = localStorage.getItem('settings')
  if (settings) {
    try {
      const parsed = JSON.parse(settings)
      return parsed.osPlacesApiKey
    } catch (e) {
      return null
    }
  }
  return null
}

// Geocode UK address + postcode to coordinates
export async function geocodeAddress(address, postcode) {
  try {
    // Build search query - UK postcodes are very accurate
    const query = `${address}, ${postcode}, UK`

    const params = new URLSearchParams({
      q: query,
      format: 'json',
      limit: '1',
      countrycodes: 'gb', // UK only
      addressdetails: '1'
    })

    const response = await fetch(`${NOMINATIM_URL}?${params}`, {
      headers: {
        'User-Agent': 'ClystcoreSurveyors/1.0' // Nominatim requires User-Agent
      }
    })

    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.status}`)
    }

    const results = await response.json()

    if (results.length === 0) {
      throw new Error('Address not found')
    }

    const result = results[0]

    return {
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
      displayName: result.display_name,
      success: true
    }
  } catch (error) {
    console.error('Geocoding error:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

// Search UK addresses with autocomplete using OS Places API
async function searchWithOSPlaces(query, apiKey) {
  const params = new URLSearchParams({
    query: query,
    key: apiKey,
    maxresults: '8',
    dataset: 'DPA,LPI', // Delivery Point Address and Land & Property Identifier
    output_srs: 'WGS84' // Standard GPS coordinates
  })

  const response = await fetch(`${OS_PLACES_URL}/find?${params}`)

  if (!response.ok) {
    throw new Error(`OS Places API failed: ${response.status}`)
  }

  const data = await response.json()

  if (!data.results || data.results.length === 0) {
    return []
  }

  return data.results.map(result => {
    const dpa = result.DPA || result.LPI
    const addressParts = []

    // Build address from components
    if (dpa.BUILDING_NUMBER) addressParts.push(dpa.BUILDING_NUMBER)
    if (dpa.BUILDING_NAME) addressParts.push(dpa.BUILDING_NAME)
    if (dpa.THOROUGHFARE_NAME) addressParts.push(dpa.THOROUGHFARE_NAME)

    const address = addressParts.join(' ')

    return {
      display: dpa.ADDRESS,
      address: address || dpa.ADDRESS.split(',')[0],
      postcode: dpa.POSTCODE || '',
      latitude: result.DPA?.LAT || result.LPI?.LAT,
      longitude: result.DPA?.LNG || result.LPI?.LNG,
      fullAddress: dpa.ADDRESS,
      uprn: dpa.UPRN // Unique Property Reference Number
    }
  })
}

// Fallback to Nominatim if OS Places not available
async function searchWithNominatim(query) {
  const params = new URLSearchParams({
    q: `${query}, UK`,
    format: 'json',
    limit: '8',
    countrycodes: 'gb',
    addressdetails: '1'
  })

  const response = await fetch(`${NOMINATIM_URL}?${params}`, {
    headers: {
      'User-Agent': 'ClystcoreSurveyors/1.0'
    }
  })

  if (!response.ok) {
    throw new Error(`Search failed: ${response.status}`)
  }

  const results = await response.json()

  return results.map(result => ({
    display: result.display_name,
    address: result.address?.road || result.address?.house_number || result.display_name.split(',')[0],
    postcode: result.address?.postcode || '',
    latitude: parseFloat(result.lat),
    longitude: parseFloat(result.lon),
    fullAddress: result.display_name
  }))
}

// Search UK addresses with autocomplete
export async function searchUKAddresses(query) {
  try {
    // Don't search if query is too short
    if (!query || query.trim().length < 3) {
      return []
    }

    const osKey = getOSPlacesKey()

    // Try OS Places API first if key is available
    if (osKey) {
      try {
        const results = await searchWithOSPlaces(query, osKey)
        if (results.length > 0) {
          console.log('Using OS Places API for address search')
          return results
        }
      } catch (error) {
        console.warn('OS Places API failed, falling back to Nominatim:', error)
      }
    }

    // Fallback to Nominatim
    console.log('Using Nominatim for address search')
    return await searchWithNominatim(query)
  } catch (error) {
    console.error('Address search error:', error)
    return []
  }
}

// Validate UK postcode format (basic)
export function isValidUKPostcode(postcode) {
  const ukPostcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i
  return ukPostcodeRegex.test(postcode.trim())
}

export default {
  geocodeAddress,
  searchUKAddresses,
  isValidUKPostcode
}
